import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';

// ─── localStorage keys ────────────────────────────────────────────────────────

const LS_KEYS = {
    ads:     'dismiss_step_ads_api',
    expense: 'dismiss_step_expense',
    cogs:    'dismiss_step_cogs_tax',
};

const readDismissed = () => ({
    ads:     localStorage.getItem(LS_KEYS.ads)     === 'true',
    expense: localStorage.getItem(LS_KEYS.expense) === 'true',
    cogs:    localStorage.getItem(LS_KEYS.cogs)    === 'true',
});

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
    {
        id:         1,
        dismissKey: null,
        title:      'Connect Seller Central',
        label:      'Step 01',
        iconClass:  'ki-directbox-default',
        description:'Link your Amazon Selling Partner account. Required before any other step.',
        ctaLabel:   'Connect Seller Central',
        ctaLink:    '/settings/marketplace-credentials?tab=spApi&creds=true',
        mandatory:  true,
    },
    {
        id:         2,
        dismissKey: 'ads',
        title:      'Connect Campaign Manager',
        label:      'Step 02',
        iconClass:  'ki-notification-on',
        description:'Link your Amazon Ads account to track PPC performance and ad spend.',
        ctaLabel:   'Connect Campaign Manager',
        ctaLink:    '/settings/marketplace-credentials?tab=advertising&creds=true',
        mandatory:  false,
    },
    {
        id:         3,
        dismissKey: 'cogs',
        title:      'Add COGS & Tax',
        label:      'Step 03',
        iconClass:  'ki-finance-calculator',
        description:'Enter cost of goods sold and tax rates to enable accurate margin calculations.',
        ctaLabel:   'Add COGS & Tax',
        ctaLink:    '/settings/products',
        mandatory:  false,
    },
    {
        id:         4,
        dismissKey: 'expense',
        title:      'Add Expense Data',
        label:      'Step 04',
        iconClass:  'ki-wallet',
        description:'Add business expenses so profit reports reflect all operational costs.',
        ctaLabel:   'Add Expenses',
        ctaLink:    '/settings/expenses',
        mandatory:  false,
    }
];

const getCompletionStatus = (userData) => ({
    1: userData?.user?.user_data?.is_amz_sp_api_connected     === 1,
    2: userData?.user?.user_data?.is_amz_ads_connected        === 1,
    3: userData?.user?.user_data?.is_expense_data_added       === true,
    4: userData?.user?.user_data?.is_product_cogs_n_tax_added === true,
});

// ─── SetupStepsBanner ─────────────────────────────────────────────────────────

const SetupStepsBanner = ({ userData }) => {
    const completed = useMemo(() => getCompletionStatus(userData), [userData]);
    const [dismissed, setDismissed] = useState(() => readDismissed());
    const [currentStep, setCurrentStep] = useState(1);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const step1Done = completed[1];

    const visibleSteps = useMemo(() =>
            STEPS.filter(s => !completed[s.id] && !(s.dismissKey && dismissed[s.dismissKey])),
        [completed, dismissed]
    );

    const allDone = useMemo(() =>
            completed[1] &&
            (completed[2] || dismissed.ads) &&
            (completed[3] || dismissed.expense) &&
            (completed[4] || dismissed.cogs),
        [completed, dismissed]
    );

    // Auto-advance to first incomplete step
    useEffect(() => {
        if (!step1Done) { setCurrentStep(1); return; }
        if (!visibleSteps.find(s => s.id === currentStep) && visibleSteps[0]) {
            setCurrentStep(visibleSteps[0].id);
        }
    }, [completed, dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDismiss = useCallback((dismissKey) => {
        localStorage.setItem(LS_KEYS[dismissKey], 'true');
        setDismissed(prev => ({ ...prev, [dismissKey]: true }));
        const remaining = visibleSteps.filter(s => s.dismissKey !== dismissKey);
        if (remaining.length > 0) setCurrentStep(remaining[0].id);
    }, [visibleSteps]);

    const handlePrev = () => {
        const idx = STEPS.findIndex(s => s.id === currentStep);
        if (idx > 0) setCurrentStep(STEPS[idx - 1].id);
    };

    const handleNext = () => {
        const idx = STEPS.findIndex(s => s.id === currentStep);
        if (idx < STEPS.length - 1) setCurrentStep(STEPS[idx + 1].id);
    };

    const canGoPrev = STEPS.findIndex(s => s.id === currentStep) > 0;
    const canGoNext = STEPS.findIndex(s => s.id === currentStep) < STEPS.length - 1;

    const activeStep    = STEPS.find(s => s.id === currentStep);
    const isCurrentDone = completed[currentStep];
    const isCurrentDis  = activeStep?.dismissKey ? dismissed[activeStep.dismissKey] : false;
    const isLocked      = !step1Done && currentStep > 1;

    const handledCount = STEPS.filter(s =>
        completed[s.id] || (s.dismissKey && dismissed[s.dismissKey])
    ).length;

    if (allDone) return null;

    return (
        <div
            style={{
                width:        '100%',
                background:   '#fff',
                border:       '1px solid #f0f0f0',
                borderRadius: 10,
                boxShadow:    '0 1px 8px rgba(0,0,0,0.06)',
                marginBottom: 20,
                overflow:     'hidden',
            }}
        >
            <style>{`
                @keyframes sbFade {
                    from { opacity: 0; transform: translateX(6px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                
                @media (max-width: 768px) {
                    .mobile-steps-menu {
                        position: fixed;
                        bottom: 0;
                        left: 0;
                        right: 0;
                        background: white;
                        border-top: 1px solid #f0f0f0;
                        box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
                        transform: translateY(100%);
                        transition: transform 0.3s ease;
                        z-index: 1000;
                    }
                    
                    .mobile-steps-menu.open {
                        transform: translateY(0);
                    }
                    
                    .mobile-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(0,0,0,0.5);
                        z-index: 999;
                    }
                }
            `}</style>

            {/* Desktop Layout (hidden on mobile) */}
            <div className="desktop-layout" style={{ display: 'flex', flexDirection: 'column' }}>
                {/* ── Single compact row for desktop ── */}
                <div
                    style={{
                        display:     'flex',
                        alignItems:  'center',
                        gap:         0,
                        minHeight:   64,
                    }}
                >
                    {/* Left — label + progress */}
                    <div
                        style={{
                            background:  '#314F71',
                            padding:     '12px 20px',
                            display:     'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignSelf:   'stretch',
                            minWidth:    140,
                            flexShrink:  0,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                            <i className="ki-outline ki-abstract-14" style={{ fontSize: 14, color: '#fff' }} />
                            <span style={{ color: '#fff', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
                                Setup Guide
                            </span>
                        </div>
                        {/* Mini progress dots */}
                        <div style={{ display: 'flex', gap: 4 }}>
                            {STEPS.map(s => {
                                const done = completed[s.id];
                                const dis  = s.dismissKey && dismissed[s.dismissKey];
                                const active = s.id === currentStep;
                                return (
                                    <Tooltip
                                        key={s.id}
                                        title={!step1Done && s.id > 1 ? 'Connect SP API first' : s.title}
                                        placement="bottom"
                                    >
                                        <button
                                            onClick={() => (!step1Done && s.id > 1) ? undefined : setCurrentStep(s.id)}
                                            style={{
                                                width:        active ? 18 : 8,
                                                height:       6,
                                                borderRadius: 3,
                                                border:       'none',
                                                padding:      0,
                                                cursor:       (!step1Done && s.id > 1) ? 'not-allowed' : 'pointer',
                                                background:   done || dis
                                                    ? '#52c41a'
                                                    : active
                                                        ? '#f45b68'
                                                        : 'rgba(255,255,255,0.25)',
                                                transition:   'all 0.25s ease',
                                                opacity:      (!step1Done && s.id > 1) ? 0.4 : 1,
                                                flexShrink:   0,
                                            }}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, marginTop: 5, whiteSpace: 'nowrap' }}>
                            {handledCount} / {STEPS.length} done
                        </div>
                    </div>

                    {/* Divider */}
                    <div style={{ width: 1, alignSelf: 'stretch', background: '#f0f0f0', flexShrink: 0 }} />

                    {/* Centre — step content (animated on step change) */}
                    <div
                        key={currentStep}
                        style={{
                            flex:      1,
                            minWidth:  0,
                            padding:   '12px 20px',
                            animation: 'sbFade 0.2s ease',
                            display:   'flex',
                            alignItems: 'center',
                            gap:       14,
                        }}
                    >
                        {/* Step icon */}
                        <span style={{ fontSize: 22, flexShrink: 0, opacity: isLocked ? 0.3 : 1, display: 'inline-flex' }}>
                            <i
                                className={`ki-outline ${isCurrentDone ? 'ki-check-circle' : isCurrentDis ? 'ki-information-5' : activeStep?.iconClass}`}
                                style={{ fontSize: 22, color: isCurrentDone ? '#52c41a' : isCurrentDis ? '#8c8c8c' : '#314F71' }}
                            />
                        </span>

                        {/* Text */}
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                                <span
                                    style={{
                                        fontSize:      10,
                                        fontWeight:    700,
                                        color:         '#dc3545',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        whiteSpace:    'nowrap',
                                    }}
                                >
                                    {activeStep?.label}
                                </span>
                                {activeStep?.mandatory && (
                                    <span
                                        style={{
                                            fontSize:     9,
                                            background:   '#dc3545',
                                            color:        '#fff',
                                            padding:      '1px 5px',
                                            borderRadius: 3,
                                            fontWeight:   700,
                                            whiteSpace:   'nowrap',
                                        }}
                                    >
                                        MANDATORY
                                    </span>
                                )}
                                {(isCurrentDone || isCurrentDis) && (
                                    <span
                                        style={{
                                            fontSize:     9,
                                            background:   isCurrentDone ? '#52c41a' : '#8c8c8c',
                                            color:        '#fff',
                                            padding:      '1px 6px',
                                            borderRadius: 10,
                                            fontWeight:   700,
                                            textTransform: 'uppercase',
                                            whiteSpace:   'nowrap',
                                        }}
                                    >
                                        {isCurrentDone ? 'Done' : 'Skipped'}
                                    </span>
                                )}
                            </div>

                            <div
                                style={{
                                    fontSize:    13,
                                    fontWeight:  700,
                                    color:       isLocked ? '#bbb' : '#1e1e2d',
                                    whiteSpace:  'nowrap',
                                    overflow:    'hidden',
                                    textOverflow:'ellipsis',
                                }}
                            >
                                {activeStep?.title}
                            </div>

                            <div
                                style={{
                                    fontSize:     11,
                                    color:        isLocked ? '#ccc' : '#888',
                                    marginTop:    2,
                                    whiteSpace:   'nowrap',
                                    overflow:     'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {isCurrentDone
                                    ? 'This step is complete — you\'re all set!'
                                    : isCurrentDis
                                        ? 'Skipped. Revisit anytime from Settings.'
                                        : activeStep?.description}
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div style={{ width: 1, alignSelf: 'stretch', background: '#f0f0f0', flexShrink: 0 }} />

                    {/* Right — CTA + dismiss + navigation */}
                    <div
                        style={{
                            display:    'flex',
                            alignItems: 'center',
                            gap:        10,
                            padding:    '12px 16px',
                            flexShrink: 0,
                        }}
                    >
                        {/* CTA button */}
                        {!isCurrentDone && !isCurrentDis && (
                            isLocked ? (
                                <Tooltip title="Please connect Seller Central first">
                                    <span
                                        style={{
                                            display:      'inline-flex',
                                            alignItems:   'center',
                                            gap:          5,
                                            padding:      '7px 14px',
                                            borderRadius: 6,
                                            background:   '#f5f5f5',
                                            color:        '#bbb',
                                            fontSize:     12,
                                            fontWeight:   600,
                                            cursor:       'not-allowed',
                                            border:       '1px solid #e8e8e8',
                                            whiteSpace:   'nowrap',
                                        }}
                                    >
                                        {activeStep?.ctaLabel}
                                        <i className="ki-outline ki-arrow-right" style={{ fontSize: 10 }} />
                                    </span>
                                </Tooltip>
                            ) : (
                                <Link
                                    to={activeStep?.ctaLink}
                                    style={{
                                        display:        'inline-flex',
                                        alignItems:     'center',
                                        gap:            5,
                                        padding:        '7px 14px',
                                        borderRadius:   6,
                                        background:     '#dc3545',
                                        color:          '#fff',
                                        fontSize:       12,
                                        fontWeight:     600,
                                        textDecoration: 'none',
                                        whiteSpace:     'nowrap',
                                        boxShadow:      '0 2px 6px rgba(220,53,69,0.25)',
                                        transition:     'opacity 0.15s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                >
                                    {activeStep?.ctaLabel}
                                    <i className="ki-outline ki-arrow-right" style={{ fontSize: 10 }} />
                                </Link>
                            )
                        )}

                        {/* Don't remind me */}
                        {!activeStep?.mandatory && !isLocked && !isCurrentDone && !isCurrentDis && (
                            <button
                                onClick={() => handleDismiss(activeStep.dismissKey)}
                                style={{
                                    background:     'none',
                                    border:         'none',
                                    padding:        0,
                                    fontSize:       11,
                                    color:          '#bbb',
                                    cursor:         'pointer',
                                    textDecoration: 'underline',
                                    fontFamily:     'inherit',
                                    whiteSpace:     'nowrap',
                                }}
                            >
                                Don't remind me again
                            </button>
                        )}

                        {/* Divider */}
                        <div style={{ width: 1, height: 28, background: '#f0f0f0', flexShrink: 0 }} />

                        {/* Prev / Next */}
                        <button
                            onClick={handlePrev}
                            disabled={!canGoPrev}
                            style={{
                                width:        28,
                                height:       28,
                                borderRadius: '50%',
                                border:       'none',
                                background:   canGoPrev ? '#314F71' : '#d9d9d9',
                                display:      'flex',
                                alignItems:   'center',
                                justifyContent: 'center',
                                cursor:       canGoPrev ? 'pointer' : 'not-allowed',
                                padding:      0,
                                flexShrink:   0,
                                transition:   'background 0.15s',
                            }}
                        >
                            <i className="ki-outline ki-arrow-left" style={{ fontSize: 20, color: '#fff' }} />
                        </button>

                        <button
                            onClick={handleNext}
                            disabled={!canGoNext}
                            style={{
                                width:        28,
                                height:       28,
                                borderRadius: '50%',
                                border:       'none',
                                background:   canGoNext ? '#314F71' : '#d9d9d9',
                                display:      'flex',
                                alignItems:   'center',
                                justifyContent: 'center',
                                cursor:       canGoNext ? 'pointer' : 'not-allowed',
                                padding:      0,
                                flexShrink:   0,
                                transition:   'background 0.15s',
                            }}
                        >
                            <i className="ki-outline ki-arrow-right" style={{ fontSize: 20, color: '#fff' }} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Layout (visible on mobile, hidden on desktop) */}
            <div className="mobile-layout" style={{ display: 'none' }}>
                {/* Mobile Header */}
                <div
                    style={{
                        background: '#314F71',
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <i className="ki-outline ki-abstract-14" style={{ fontSize: 14, color: '#fff' }} />
                            <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>
                                Setup Guide
                            </span>
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, marginTop: 4 }}>
                            {handledCount} / {STEPS.length} done
                        </div>
                    </div>
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        style={{
                            background: 'rgba(255,255,255,0.1)',
                            border: 'none',
                            borderRadius: 6,
                            padding: '6px 12px',
                            color: '#fff',
                            fontSize: 12,
                            cursor: 'pointer',
                        }}
                    >
                        {mobileMenuOpen ? 'Close' : 'Steps'}
                    </button>
                </div>

                {/* Mobile Content */}
                <div style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <span style={{ fontSize: 28, flexShrink: 0, opacity: isLocked ? 0.3 : 1, display: 'inline-flex' }}>
                            <i
                                className={`ki-outline ${isCurrentDone ? 'ki-check-circle' : isCurrentDis ? 'ki-information-5' : activeStep?.iconClass}`}
                                style={{ fontSize: 28, color: isCurrentDone ? '#52c41a' : isCurrentDis ? '#8c8c8c' : '#314F71' }}
                            />
                        </span>
                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: '#dc3545',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {activeStep?.label}
                                </span>
                                {activeStep?.mandatory && (
                                    <span
                                        style={{
                                            fontSize: 10,
                                            background: '#dc3545',
                                            color: '#fff',
                                            padding: '2px 8px',
                                            borderRadius: 3,
                                            fontWeight: 700,
                                        }}
                                    >
                                        MANDATORY
                                    </span>
                                )}
                                {(isCurrentDone || isCurrentDis) && (
                                    <span
                                        style={{
                                            fontSize: 10,
                                            background: isCurrentDone ? '#52c41a' : '#8c8c8c',
                                            color: '#fff',
                                            padding: '2px 8px',
                                            borderRadius: 10,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {isCurrentDone ? 'Done' : 'Skipped'}
                                    </span>
                                )}
                            </div>
                            <div
                                style={{
                                    fontSize: 16,
                                    fontWeight: 700,
                                    color: isLocked ? '#bbb' : '#1e1e2d',
                                    marginBottom: 4,
                                }}
                            >
                                {activeStep?.title}
                            </div>
                            <div
                                style={{
                                    fontSize: 13,
                                    color: isLocked ? '#ccc' : '#888',
                                    lineHeight: 1.4,
                                }}
                            >
                                {isCurrentDone
                                    ? 'This step is complete — you\'re all set!'
                                    : isCurrentDis
                                        ? 'Skipped. Revisit anytime from Settings.'
                                        : activeStep?.description}
                            </div>
                        </div>
                    </div>

                    {/* Mobile Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                        {!isCurrentDone && !isCurrentDis && (
                            isLocked ? (
                                <Tooltip title="Please connect SP API first">
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 8,
                                            padding: '10px 16px',
                                            borderRadius: 6,
                                            background: '#f5f5f5',
                                            color: '#bbb',
                                            fontSize: 13,
                                            fontWeight: 600,
                                            cursor: 'not-allowed',
                                            border: '1px solid #e8e8e8',
                                            width: '100%',
                                        }}
                                    >
                                        {activeStep?.ctaLabel}
                                        <i className="ki-outline ki-arrow-right" />
                                    </span>
                                </Tooltip>
                            ) : (
                                <Link
                                    to={activeStep?.ctaLink}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 8,
                                        padding: '10px 16px',
                                        borderRadius: 6,
                                        background: '#dc3545',
                                        color: '#fff',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        textDecoration: 'none',
                                        textAlign: 'center',
                                    }}
                                >
                                    {activeStep?.ctaLabel}
                                    <i className="ki-outline ki-arrow-right" />
                                </Link>
                            )
                        )}

                        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                            <button
                                onClick={handlePrev}
                                disabled={!canGoPrev}
                                style={{
                                    flex: 1,
                                    padding: '8px 16px',
                                    borderRadius: 6,
                                    border: 'none',
                                    background: canGoPrev ? '#314F71' : '#d9d9d9',
                                    color: '#fff',
                                    cursor: canGoPrev ? 'pointer' : 'not-allowed',
                                    fontSize: 13,
                                    fontWeight: 600,
                                }}
                            >
                                <i className="ki-outline ki-arrow-left" /> Previous
                            </button>
                            <button
                                onClick={handleNext}
                                disabled={!canGoNext}
                                style={{
                                    flex: 1,
                                    padding: '8px 16px',
                                    borderRadius: 6,
                                    border: 'none',
                                    background: canGoNext ? '#314F71' : '#d9d9d9',
                                    color: '#fff',
                                    cursor: canGoNext ? 'pointer' : 'not-allowed',
                                    fontSize: 13,
                                    fontWeight: 600,
                                }}
                            >
                                Next <i className="ki-outline ki-arrow-right" />
                            </button>
                        </div>

                        {!activeStep?.mandatory && !isLocked && !isCurrentDone && !isCurrentDis && (
                            <button
                                onClick={() => handleDismiss(activeStep.dismissKey)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '6px',
                                    fontSize: 12,
                                    color: '#bbb',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                }}
                            >
                                Don't remind me again
                            </button>
                        )}
                    </div>
                </div>

                {/* Mobile Steps Menu */}
                {mobileMenuOpen && (
                    <>
                        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
                        <div className={`mobile-steps-menu ${mobileMenuOpen ? 'open' : ''}`}>
                            <div style={{ padding: '16px' }}>
                                <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>All Steps</div>
                                {STEPS.map(step => {
                                    const isComplete = completed[step.id];
                                    const isSkipped = step.dismissKey && dismissed[step.dismissKey];
                                    const isActive = step.id === currentStep;
                                    const isStepLocked = !step1Done && step.id > 1;

                                    return (
                                        <button
                                            key={step.id}
                                            onClick={() => {
                                                if (!isStepLocked) {
                                                    setCurrentStep(step.id);
                                                    setMobileMenuOpen(false);
                                                }
                                            }}
                                            disabled={isStepLocked}
                                            style={{
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '12px',
                                                marginBottom: '8px',
                                                borderRadius: '8px',
                                                border: isActive ? '2px solid #dc3545' : '1px solid #f0f0f0',
                                                background: isActive ? '#fff5f5' : '#fff',
                                                cursor: isStepLocked ? 'not-allowed' : 'pointer',
                                                opacity: isStepLocked ? 0.5 : 1,
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <span style={{ fontSize: 20, display: 'inline-flex' }}>
                                                    <i className={`ki-outline ${step.iconClass}`} style={{ fontSize: 20, color: '#314F71' }} />
                                                </span>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 700, fontSize: 13 }}>{step.title}</div>
                                                    <div style={{ fontSize: 11, color: '#888' }}>{step.label}</div>
                                                </div>
                                                {(isComplete || isSkipped) && (
                                                    <span style={{ fontSize: 12, color: isComplete ? '#52c41a' : '#8c8c8c' }}>
                                                        {isComplete ? 'Done' : 'Skipped'}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* ── Thin progress bar at the very bottom ── */}
            <div style={{ height: 3, background: '#f5f5f5' }}>
                <div
                    style={{
                        height:     '100%',
                        width:      `${(handledCount / STEPS.length) * 100}%`,
                        background: 'linear-gradient(90deg, #f45b68, #dc3545)',
                        transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                />
            </div>

            {/* Media query styles */}
            <style jsx>{`
                @media (max-width: 768px) {
                    .desktop-layout {
                        display: none !important;
                    }
                    .mobile-layout {
                        display: block !important;
                    }
                }
                @media (min-width: 769px) {
                    .desktop-layout {
                        display: block !important;
                    }
                    .mobile-layout {
                        display: none !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default SetupStepsBanner;